import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { NetworkConstants } from '../../utils/NetworkConstants'
import { createPathWithLang } from '../../utils/languagePathUtils'
import {
  AiBrief,
  DimensionMetrics,
  LeaderMetrics,
  MetricsOverview,
  ReviewMetrics,
  TIME_RANGE_LABELS,
  TimeRange,
  formatDateLabel,
} from '../../types/metrics'
import { exportLeaderMetricsCSV } from '../../services/MetricsDashboardService'
import { completionRate as calcCompletionRate, safePercentage, totalActivity } from '../../utils/dashboardCalculations'
import useUserMetricsQuery from '../../hooks/useUserMetricsQuery'
import useDashboardQueries from '../../hooks/useDashboardQueries'
import { AppButton } from '../atoms/AppButton/AppButton'
import AppTooltip from '../atoms/AppTooltip'
import InfoTooltip from '../atoms/InfoTooltip'
import AiBriefCard, { AiBriefSkeleton } from '../molecules/dashboard/AiBriefCard'
import CategoryMatrixTable, { CategoryMatrixTableSkeleton } from '../molecules/dashboard/CategoryMatrixTable'
import DimensionAveragesChart, { DimensionAveragesChartSkeleton } from '../molecules/dashboard/DimensionAveragesChart'
import KpiCard, { KpiCardSkeleton } from '../molecules/dashboard/KpiCard'
import LeaderBreakdownTable, { LeaderBreakdownTableSkeleton } from '../molecules/dashboard/LeaderBreakdownTable'
import TalentRankGrid, { TalentRankGridSkeleton } from '../molecules/dashboard/TalentRankGrid'
import TimeRangeDropdown from '../molecules/dashboard/TimeRangeDropdown'
import UserMetricsTable from '../molecules/dashboard/UserMetricsTable'

type QueryState<T> = { data: T | null; loading: boolean; error: string | null }

const timeAgoLabel = (lastRefreshed: Date): string => {
  const seconds = Math.round((Date.now() - lastRefreshed.getTime()) / 1000)
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  return `${Math.round(seconds / 60)}m ago`
}

const buildTimeRangeLabel = (timeRange: TimeRange, customAfter: string, customBefore: string): string => {
  if (timeRange === 'custom' && customAfter && customBefore) {
    return `${formatDateLabel(customAfter)} – ${formatDateLabel(customBefore)}`
  }
  return TIME_RANGE_LABELS[timeRange]
}

const ErrorAlert = ({ message, className = '' }: { message: string; className?: string }): JSX.Element => (
  <p
    role="alert"
    className={`rounded-lg border border-light-red bg-light-red px-4 py-3 text-xl text-error-red ${className}`}
  >
    {message}
  </p>
)

const SectionHeading = ({ title, tooltip }: { title: string; tooltip: string }): JSX.Element => (
  <div className="flex items-center gap-2">
    <h2 className="m-0 text-3xl font-bold leading-none text-dark-blue">{title}</h2>
    <InfoTooltip text={tooltip} />
  </div>
)

const PLATFORM_TOOLTIP = 'All-time platform stats: total users, login activity, and recent password changes.'
const REVIEW_ACTIVITY_TOOLTIP = 'Reviews completed, in progress, and skipped within the selected time range.'

const PageHeader = ({
  isLoading,
  lastRefreshed,
  timeRange,
  customAfter,
  customBefore,
  timeRangeLabel,
  onRefresh,
  onPresetChange,
  onCustomApply,
}: {
  isLoading: boolean
  lastRefreshed: Date
  timeRange: TimeRange
  customAfter: string
  customBefore: string
  timeRangeLabel: string
  onRefresh: () => void
  onPresetChange: (range: TimeRange) => void
  onCustomApply: (after: string, before: string) => void
}): JSX.Element => (
  <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
    <div className="flex min-w-0 flex-nowrap items-center gap-4">
      <h1 className="whitespace-nowrap text-5xl font-semibold text-dark-blue">System Dashboard</h1>
      <Link to={createPathWithLang(NetworkConstants.URL_ADMIN_COMPLETED_REVIEWS)}>
        <AppButton type="clear">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span className="whitespace-nowrap">View completed reviews</span>
        </AppButton>
      </Link>
      <Link to={createPathWithLang(NetworkConstants.URL_ADMIN_SKIPPED_REVIEWS)}>
        <AppButton type="clear">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
          <span className="whitespace-nowrap">View skipped reviews</span>
        </AppButton>
      </Link>
      <span className="whitespace-nowrap text-xl text-stone-gray">Updated {timeAgoLabel(lastRefreshed)}</span>
      <AppTooltip
        title={<span style={{ display: 'block', fontSize: '14px', lineHeight: 1.45 }}>Refresh dashboard data</span>}
        placement="top"
        arrow
      >
        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          aria-label="Refresh dashboard data"
          className="inline-flex items-center justify-center rounded-full p-1 text-stone-gray hover:text-dark-blue disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </AppTooltip>
    </div>

    <div className="flex items-center gap-3">
      <span className="whitespace-nowrap text-xl text-stone-gray">Date range:</span>
      <TimeRangeDropdown
        timeRange={timeRange}
        customAfter={customAfter}
        customBefore={customBefore}
        triggerLabel={timeRangeLabel}
        onPresetChange={onPresetChange}
        onCustomApply={onCustomApply}
      />
    </div>
  </header>
)

const CohortBriefSection = ({
  brief,
  timeRangeLabel,
}: {
  brief: QueryState<AiBrief>
  timeRangeLabel: string
}): JSX.Element => (
  <section aria-label="Cohort summary">
    {brief.error && <ErrorAlert message={brief.error} className="mb-4" />}
    {brief.loading && <AiBriefSkeleton timeRangeLabel={timeRangeLabel} />}
    {!brief.loading && brief.data && <AiBriefCard brief={brief.data} timeRangeLabel={timeRangeLabel} />}
  </section>
)

const PlatformOverviewBody = ({ data }: { data: MetricsOverview }): JSX.Element => (
  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
    <KpiCard label="Total users" value={data.totalUsers} />
    <KpiCard
      label="Logged in"
      value={data.usersLoggedIn}
      sub={`${safePercentage(data.usersLoggedIn, data.totalUsers)}% of total`}
    />
    <KpiCard
      label="Never logged in"
      value={data.usersNeverLoggedIn}
      sub={`${safePercentage(data.usersNeverLoggedIn, data.totalUsers)}% of total`}
    />
    <KpiCard label={`Password changed (${data.passwordChangeWindowDays}d)`} value={data.usersChangedPasswordRecently} />
  </div>
)

const PlatformOverviewSkeleton = (): JSX.Element => (
  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
    <KpiCardSkeleton />
    <KpiCardSkeleton />
    <KpiCardSkeleton />
    <KpiCardSkeleton />
  </div>
)

const PlatformOverviewSection = ({ overview }: { overview: QueryState<MetricsOverview> }): JSX.Element => (
  <section aria-label="Platform overview" className="flex flex-col gap-4">
    {overview.error && <ErrorAlert message={overview.error} />}
    <SectionHeading title="Platform" tooltip={PLATFORM_TOOLTIP} />
    {overview.loading && <PlatformOverviewSkeleton />}
    {!overview.loading && overview.data && <PlatformOverviewBody data={overview.data} />}
  </section>
)

const ReviewActivityBody = ({ data }: { data: ReviewMetrics }): JSX.Element => {
  const total = totalActivity(data.reviewsCompleted, data.reviewsInProgress, data.reviewsSkipped)
  const completion = calcCompletionRate(data.reviewsCompleted, data.reviewsInProgress, data.reviewsSkipped)
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
      <KpiCard label="Completed" value={data.reviewsCompleted} />
      <KpiCard label="In progress" value={data.reviewsInProgress} />
      <KpiCard label="Skipped" value={data.reviewsSkipped} />
      <KpiCard label="Total activity" value={total} />
      <KpiCard label="Completion rate" value={`${completion}%`} />
    </div>
  )
}

const ReviewActivitySkeleton = (): JSX.Element => (
  <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
    <KpiCardSkeleton />
    <KpiCardSkeleton />
    <KpiCardSkeleton />
    <KpiCardSkeleton />
    <KpiCardSkeleton />
  </div>
)

const ReviewActivitySection = ({
  reviews,
  timeRangeLabel,
}: {
  reviews: QueryState<ReviewMetrics>
  timeRangeLabel: string
}): JSX.Element => (
  <section aria-label={`Review activity — ${timeRangeLabel}`} className="flex flex-col gap-4">
    {reviews.error && <ErrorAlert message={reviews.error} />}
    <SectionHeading title={`Review activity — ${timeRangeLabel}`} tooltip={REVIEW_ACTIVITY_TOOLTIP} />
    {reviews.loading && <ReviewActivitySkeleton />}
    {!reviews.loading && reviews.data && <ReviewActivityBody data={reviews.data} />}
  </section>
)

const TalentRankSlot = ({ reviews }: { reviews: QueryState<ReviewMetrics> }): JSX.Element | null => {
  if (reviews.loading) return <TalentRankGridSkeleton />
  if (reviews.data) return <TalentRankGrid metrics={reviews.data} />
  return null
}

const DimensionsSlot = ({ dimensions }: { dimensions: QueryState<DimensionMetrics> }): JSX.Element | null => {
  if (dimensions.error) return <ErrorAlert message={dimensions.error} />
  if (dimensions.loading) return <DimensionAveragesChartSkeleton />
  if (dimensions.data) return <DimensionAveragesChart metrics={dimensions.data} />
  return null
}

const ChartsSection = ({
  reviews,
  dimensions,
  timeRangeLabel,
}: {
  reviews: QueryState<ReviewMetrics>
  dimensions: QueryState<DimensionMetrics>
  timeRangeLabel: string
}): JSX.Element => (
  <section aria-label={`Talent and dimensions — ${timeRangeLabel}`}>
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <TalentRankSlot reviews={reviews} />
      <DimensionsSlot dimensions={dimensions} />
    </div>
  </section>
)

const CategoryMatrixSection = ({ dimensions }: { dimensions: QueryState<DimensionMetrics> }): JSX.Element => {
  const showTable = !dimensions.loading && dimensions.data && dimensions.data.byCategory.length > 0
  return (
    <section aria-label="Scores by category">
      {dimensions.loading && <CategoryMatrixTableSkeleton />}
      {showTable && dimensions.data && <CategoryMatrixTable rows={dimensions.data.byCategory} />}
    </section>
  )
}

const LeadersSection = ({ leaders }: { leaders: QueryState<LeaderMetrics[]> }): JSX.Element => (
  <section aria-label="Leaders breakdown" className="flex flex-col gap-4">
    {leaders.error && <ErrorAlert message={leaders.error} />}
    {leaders.loading && <LeaderBreakdownTableSkeleton />}
    {!leaders.loading && leaders.data && (
      <LeaderBreakdownTable leaders={leaders.data} onExport={exportLeaderMetricsCSV} />
    )}
  </section>
)

const ReviewerSection = ({ users }: { users: ReturnType<typeof useUserMetricsQuery> }): JSX.Element => (
  <section aria-label="Reviewer breakdown" className="flex flex-col gap-4">
    {users.error && <ErrorAlert message={users.error} />}
    <UserMetricsTable
      rows={users.rows}
      totalElements={users.totalElements}
      page={users.page}
      pageSize={users.pageSize}
      onPageChange={users.onPageChange}
      onPageSizeChange={users.onPageSizeChange}
      loading={users.loading}
      search={users.search}
      onSearchChange={users.onSearchChange}
    />
  </section>
)

const AdminDashboard = (): JSX.Element => {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d')
  const [customAfter, setCustomAfter] = useState('')
  const [customBefore, setCustomBefore] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date())

  const users = useUserMetricsQuery(refreshKey)
  const { overview, reviews, dimensions, leaders, brief } = useDashboardQueries(
    timeRange,
    customAfter,
    customBefore,
    refreshKey
  )

  const isLoading =
    overview.loading || reviews.loading || users.loading || dimensions.loading || leaders.loading || brief.loading

  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1)
    setLastRefreshed(new Date())
  }, [])

  const handleCustomApply = useCallback((after: string, before: string): void => {
    setCustomAfter(after)
    setCustomBefore(before)
    setTimeRange('custom')
  }, [])

  const timeRangeLabel = buildTimeRangeLabel(timeRange, customAfter, customBefore)

  return (
    <main className="container mx-auto pb-16">
      <section className="mt-10 flex flex-col gap-12">
        <PageHeader
          isLoading={isLoading}
          lastRefreshed={lastRefreshed}
          timeRange={timeRange}
          customAfter={customAfter}
          customBefore={customBefore}
          timeRangeLabel={timeRangeLabel}
          onRefresh={handleRefresh}
          onPresetChange={setTimeRange}
          onCustomApply={handleCustomApply}
        />
        <CohortBriefSection brief={brief} timeRangeLabel={timeRangeLabel} />
        <PlatformOverviewSection overview={overview} />
        <ReviewActivitySection reviews={reviews} timeRangeLabel={timeRangeLabel} />
        <ChartsSection reviews={reviews} dimensions={dimensions} timeRangeLabel={timeRangeLabel} />
        <CategoryMatrixSection dimensions={dimensions} />
        <LeadersSection leaders={leaders} />
        <ReviewerSection users={users} />
      </section>
    </main>
  )
}

export default AdminDashboard
